import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import Decimal from 'decimal.js'
import styles from './number-input.css'
import shortId from '@/utils/short-id'
import lang from '@/lang'
import { SquareIconButton } from '../../buttons/index'
import IconAdd from '@assets/images/icons/plus2.svg'
import IconMinus from '@assets/images/icons/minus2.svg'
import { useFormikContext } from 'formik'
import debounce from 'lodash/debounce'
import { isEmpty, isNumber } from 'lodash'
import RichTooltip from '@/modules/base/components/rich-tooltip/rich-tooltip'
import WarningAmberRoundedIcon from '@mui/icons-material/WarningAmberRounded'
import HighlightOffRoundedIcon from '@mui/icons-material/HighlightOffRounded'

const trimZeros = (fixedString) => {
  if (!fixedString.includes('.')) return fixedString
  return fixedString.replace(/0+$/, '').replace(/\.$/, '')
}

const formatDecimal = (rawValue, precisionValue, trim) => {
  const fixed = parseFloat(rawValue || 0).toFixed(precisionValue)
  return trim ? trimZeros(fixed) : fixed
}

const getDecimalPlaces = (stringValue) => {
  const match = /\.(\d+)$/.exec(stringValue)
  return match ? match[1].length : 0
}

interface NumberInputProps {
  value?: string | number
  defaultValue?: number
  name?: string
  label?: string
  customClass?: string | object
  className?: string
  inputClassName?: string
  placeholder?: string
  autoComplete?: string
  dataQsId?: string
  disabled?: boolean
  autoFocus?: boolean
  required?: boolean
  showButtons?: boolean
  useFormik?: boolean
  shouldNotSetEmptyInput?: boolean
  selectOnFocus?: boolean
  precision?: number
  stepSize?: number
  min?: number
  max?: number
  maxLength?: number
  enforceMinMax?: boolean
  valueAsNumber?: boolean
  disableArrowKeys?: boolean
  disableMouseWheel?: boolean
  trimTrailingZeros?: boolean
  validationTooltip?: {
    state?: 'error' | 'warning'
    heading?: string
    body?: React.ReactNode
    placement?: 'top' | 'right' | 'bottom' | 'left'
  }
  onChange?: (value: string | number, name?: string) => void
  onBlur?: (value: string | number, name?: string) => void
  onPaste?: (value: string, name?: string) => void
  onKeyUp?: (event: React.KeyboardEvent<HTMLInputElement>, name?: string) => void
  onKeyDown?: (event: React.KeyboardEvent<HTMLInputElement>, name?: string) => void
}

const NumberInput = React.forwardRef<HTMLInputElement, NumberInputProps>(function NumberInput(props, forwardRef) {
  const {
    defaultValue,
    value: propValue,
    name,
    onChange,
    selectOnFocus,
    onPaste,
    required = false,
    onKeyUp,
    onKeyDown,
    onBlur,
    showButtons,
    precision = 0,
    useFormik,
    shouldNotSetEmptyInput,
    label,
    customClass,
    className,
    inputClassName,
    disabled,
    autoFocus,
    placeholder,
    autoComplete,
    stepSize = 1,
    min,
    max,
    enforceMinMax = true,
    dataQsId,
    valueAsNumber = false,
    disableArrowKeys = false,
    disableMouseWheel = false,
    validationTooltip,
    trimTrailingZeros = false,
  } = props
  const [value, setValue] = useState(formatDecimal(propValue, precision, trimTrailingZeros) || '')

  const formikContext = useFormik === undefined || useFormik ? useFormikContext() : null

  const field = formikContext ? formikContext.getFieldProps(name) : null
  const meta = formikContext && name ? formikContext.getFieldMeta(name) : null

  const onChangeRef = useRef()
  onChangeRef.current = onChange

  const onBlurRef = useRef()
  onBlurRef.current = onBlur

  const isValid = (value) => {
    if (typeof value === 'number') {
      return true
    }

    return (required && !!value.trim()) || !value.trim() || !isNaN(parseInt(value, 10))
  }

  useEffect(() => {
    if (defaultValue !== undefined && defaultValue !== null && defaultValue !== value) {
      setValue(formatDecimal(defaultValue, precision, trimTrailingZeros))
    }
  }, [defaultValue, precision, trimTrailingZeros])

  const handleKeyUp = (event) => {
    onKeyUp?.(event, name)
    if (event.key === 'Enter') {
      onInputBlur(event)
    }
  }

  const handleKeyDown = (event) => {
    if (disableArrowKeys && (event.key === 'ArrowUp' || event.key === 'ArrowDown')) {
      event.preventDefault()
      return
    }
    onKeyDown?.(event, name)
    if (event.key === 'Enter') {
      onInputBlur(event)
    }
  }

  const handleWheel = (event) => {
    event.preventDefault()
    if (disableMouseWheel && event && event.target && event.target.blur) {
      event.target.blur()
    }
  }

  const handlePaste = (event) => {
    const value = event.clipboardData.getData('text/plain')

    if (isValid(value) && onPaste) {
      onPaste(value, name)
    }
  }

  const onInputFocus = (event) => {
    if (!selectOnFocus) return

    event.target.select()
  }

  const getMaxOrMinValue = (value) => {
    if (isNumber(max) && value > max) {
      return max
    }
    if (isNumber(min) && value < min) {
      return min
    }

    return value
  }

  const onInputChange = useCallback((event) => {
    let { target: { value } } = event

    if (!isNaN(value) && (props.maxLength === undefined || value.length <= props.maxLength)) {
      if (enforceMinMax) {
        value = getMaxOrMinValue(value)
      }
      setValue(valueAsNumber ? +value : value)
      field?.onChange(event)
      onChangeRef.current?.(valueAsNumber ? +value : value, name)
    }
  }, [props.maxLength, name])

  const onInputBlur = useCallback((event) => {
    const { target: { value } } = event

    if (!isValid(value)) return

    // trimTrailingZeros mode: respect exactly what the user typed (e.g. "2,00" stays "2,00").
    // Only reformat (round + trim) when it's empty or has more decimals than allowed -
    // that's an automatic correction, not something the user deliberately typed.
    if (trimTrailingZeros && typeof value !== 'number') {
      const trimmedInput = value.trim()
      let finalValue
      if (!trimmedInput) {
        finalValue = shouldNotSetEmptyInput ? '' : formatDecimal(0, precision, true)
      } else if (getDecimalPlaces(trimmedInput) > precision) {
        finalValue = formatDecimal(Number.parseFloat(trimmedInput) || 0, precision, true)
      } else {
        finalValue = trimmedInput
      }

      const newValue = getMaxOrMinValue(finalValue)
      setValue(valueAsNumber ? +newValue : newValue)
      onBlurRef.current?.(valueAsNumber ? +newValue : newValue, name)
      return
    }

    let parsedValue = value
    if (typeof value !== 'number') {
      parsedValue = Number.parseFloat(value) || (!shouldNotSetEmptyInput && 0)
    }

    const newValue = getMaxOrMinValue(formatDecimal(parsedValue, precision, trimTrailingZeros))
    setValue(valueAsNumber ? +newValue : newValue)
    onBlurRef.current?.(valueAsNumber ? +newValue : newValue, name)
  }, [precision, trimTrailingZeros])

  const disabledClass = disabled ? styles.disabled : ''
  const elementId = `input-${shortId()}`

  const onInputBlurDebounce = useMemo(() => {
    return debounce((value) => onBlurRef.current && onBlurRef.current(value, name), 200)
  }, [])

  const onCountUpDown = useCallback((event, countUp) => {
    setValue(currentValue => {
      const valueAsDecimal = new Decimal(parseFloat(currentValue) || 0)
      const fixed = (countUp ? valueAsDecimal.plus(stepSize) : valueAsDecimal.minus(stepSize)).toFixed(precision)
      const newValue = trimTrailingZeros ? trimZeros(fixed) : fixed

      onChangeRef.current?.(valueAsNumber ? +newValue : newValue, name)
      if (field) {
        formikContext.getFieldHelpers(field.name).setValue(valueAsNumber ? +newValue : newValue)
        formikContext.setFieldTouched(field.name, true)
      }

      onInputBlurDebounce(valueAsNumber ? +newValue : newValue, name)
      return valueAsNumber ? +newValue : newValue
    })
  }, [name])

  const labelEl = label
    ? <label className={styles.label} htmlFor={elementId}>{label}</label>
    : null
  const hasValidationTooltip = validationTooltip && !isEmpty(validationTooltip)
  const inputClass = [
    styles.input,
    hasValidationTooltip ? styles.withValidationIndicator : '',
    hasValidationTooltip && validationTooltip?.state === 'warning' ? styles.warning : '',
    hasValidationTooltip && validationTooltip?.state !== 'warning' ? styles.invalid : '',
  ].filter(Boolean).join(' ')

  const showError = meta?.error && meta?.touched
  const errorEl = showError ? <div className={styles.error}>{meta.error}</div> : null

  return (
    <div className={`${customClass || className || ''} ${disabledClass}`}>
      {labelEl}
      <div className={styles.inputWrapper} >
        <input
          type="number"
          step="any"
          className={showError ? `${styles.input} ${inputClassName || ''} ${styles.invalid}` : `${styles.input} ${inputClassName || ''}`}
          ref={forwardRef}
          placeholder={placeholder}
          onKeyUp={handleKeyUp}
          onKeyDown={handleKeyDown}
          onPaste={handlePaste}
          onFocus={onInputFocus}
          onBlur={onInputBlur}
          onChange={onInputChange}
          onWheel={handleWheel}
          value={value}
          name={name}
          autoFocus={autoFocus}
          min={min}
          max={max}
          disabled={disabled}
          id={elementId}
          autoComplete={autoComplete}
          data-qs-id={dataQsId}
        />
        {hasValidationTooltip
          ? (
            <div className={styles.validationIndicatorWrapper}>
              <RichTooltip
                disabled={!hasValidationTooltip}
                placement={validationTooltip?.placement}
                heading={validationTooltip?.heading}
                body={validationTooltip?.body}>
                <span className={`${styles.validationIndicator} ${validationTooltip?.state === 'warning' ? styles.validationIndicatorWarning : styles.validationIndicatorError}`}>
                  {validationTooltip?.state === 'warning'
                    ? <WarningAmberRoundedIcon fontSize='inherit' />
                    : <HighlightOffRoundedIcon fontSize='inherit' />}
                </span>
              </RichTooltip>
            </div>
          )
          : null}
        {showButtons && !disabled
         && <div className={`number-buttons ${styles.buttons}`}>
           <SquareIconButton
             icon={IconAdd}
             size='smaller'
             variant='white'
             onClick={(e) => onCountUpDown(e, true)}
             title={lang.generics.plus}
             disabled={max !== undefined && value >= max}
             tabIndex={-1}
           />
           <SquareIconButton
             icon={IconMinus}
             size='smaller'
             variant='white'
             onClick={(e) => onCountUpDown(e, false)}
             title={lang.generics.minus}
             disabled={min !== undefined && value <= min}
             tabIndex={-1}
           />
         </div>
        }
      </div>
      {errorEl}
    </div>
  )
})

export default NumberInput
